import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PaperPlaneTilt, Eye, CheckCircle, XCircle, Clock, Image as ImageIcon, VideoCamera, FileText } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  portalListPublications, portalMarkPublicationRead,
  portalReservePublication, portalDeclinePublication,
  publicationMediaUrl,
} from '../services/api';

const RESOURCE_ICONS = {
  mission: FileText, dossier: FileText, document: FileText, convention: FileText, agenda_event: Clock,
  photo: ImageIcon, video: VideoCamera,
};

const STATUS_COLORS = {
  received: 'bg-blue-100 text-blue-800',
  read: 'bg-purple-100 text-purple-800',
  reserved: 'bg-amber-100 text-amber-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  expired: 'bg-gray-200 text-gray-700',
};
const STATUS_LABELS = { received: 'Reçu', read: 'Lu', reserved: 'Réservé', accepted: 'Accepté', declined: 'Refusé', expired: 'Expiré' };

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
}

export function PortalPublicationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await portalListPublications();
      setItems(r.data.items || []);
    } catch { toast.error('Erreur chargement publications'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleOpen = async (p) => {
    setOpenId(p.id);
    if (p.my_status === 'received') {
      try { await portalMarkPublicationRead(p.id); } catch {}
      load();
    }
  };
  const handleReserve = async (id) => {
    try { await portalReservePublication(id); toast.success('Réservation envoyée — en attente de validation'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const handleDecline = async (id) => {
    try { await portalDeclinePublication(id); toast.success('Refus enregistré'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="portal-publications-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
          <PaperPlaneTilt size={22} className="inline mr-2 text-[#2D5A4C]" /> Publications
        </h1>
        <p className="text-sm text-[#5E6D66]">Missions, dossiers, documents et événements partagés avec vous.</p>
      </div>

      {loading && <Loader2 className="animate-spin" />}
      {!loading && items.length === 0 && (
        <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">
          Aucune publication pour le moment.
        </CardContent></Card>
      )}

      <div className="grid gap-3">
        {items.map((p) => {
          const Icon = RESOURCE_ICONS[p.resource_type] || FileText;
          const isOpen = openId === p.id;
          return (
            <Card key={p.id} className="border-[#E8E6E1]" data-testid={`portal-pub-card-${p.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-[#2D5A4C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#1A2421]">{p.title}</h3>
                      <Badge className={`${STATUS_COLORS[p.my_status]} text-[10px]`} data-testid={`portal-pub-status-${p.id}`}>{STATUS_LABELS[p.my_status]}</Badge>
                      {p.capacity != null && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800">Capacité {p.capacity}</Badge>}
                    </div>
                    <p className="text-xs text-[#5E6D66] line-clamp-2 mt-1">{p.description || p.resource_snapshot?.title}</p>
                    <p className="text-[10px] text-[#5E6D66] mt-1">Jusqu'au {fmtDate(p.display_until)}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleOpen(p)} data-testid={`portal-pub-open-${p.id}`}>
                    <Eye size={14} />
                  </Button>
                </div>

                {isOpen && (
                  <div className="border-t border-[#E8E6E1] pt-3 space-y-2">
                    {p.media?.object_path && (
                      p.resource_type === 'photo'
                        ? <img src={publicationMediaUrl(p.media.object_path)} alt="" className="rounded max-h-64" />
                        : <video src={publicationMediaUrl(p.media.object_path)} controls className="rounded max-h-64 w-full" />
                    )}
                    {p.resource_snapshot?.subtitle && <p className="text-xs text-[#5E6D66]">Réf : {p.resource_snapshot.subtitle}</p>}
                    {!p.is_expired && p.my_status !== 'accepted' && p.my_status !== 'declined' && (
                      <div className="flex gap-2">
                        {p.my_status !== 'reserved' && (
                          <Button size="sm" onClick={() => handleReserve(p.id)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid={`portal-pub-reserve-${p.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Réserver / Postuler
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleDecline(p.id)} data-testid={`portal-pub-decline-${p.id}`}>
                          <XCircle size={14} className="mr-1" /> Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
